import { useEffect, useRef, useState } from 'react';
import { MatchScene } from '@/game/scenes/MatchScene';
import { useMatchStore } from '@/stores/matchStore';
import type { MatchBackgroundTheme } from '@/game/matchBackground';
import { HEADER_H } from '@shared/gameConfig';
import { WrTrack } from '@/game/entities/WrTrack';

interface PixiCanvasProps {
  width: number;
  height: number;
  sceneRef: React.MutableRefObject<MatchScene | null>;
  onReady?: (scene: MatchScene) => void;
  leftBackgroundTheme: MatchBackgroundTheme;
  rightBackgroundTheme: MatchBackgroundTheme;
}

/**
 * Mounts a Pixi-driven canvas of the requested logical size. The Pixi
 * Application handles its own DPR-aware backing buffer; React only manages
 * the host <canvas> element. A CSS placeholder shows the same dark wall
 * gradient + a LOADING strip while Pixi finishes async init — on a phone
 * that boot can take 1-2s and the user shouldn't see a blank rectangle.
 */
export function PixiCanvas({
  width,
  height,
  sceneRef,
  onReady,
  leftBackgroundTheme,
  rightBackgroundTheme,
}: PixiCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [ready, setReady] = useState(false);
  const gap = 4;
  const trackWidth = WrTrack.getWidth();
  const laneWidth = Math.floor((width - trackWidth - gap * 2) / 2);
  const rightLaneX = laneWidth + trackWidth + gap * 2;

  useEffect(() => {
    let mounted = true;
    let scene: MatchScene | null = null;
    if (!canvasRef.current) return;

    const s = new MatchScene();
    const { yourSeq, rivalSeq, yourPath, rivalPath } = useMatchStore.getState();

    s.init(canvasRef.current, {
      canvasWidth: width,
      canvasHeight: height,
      yourSeq: [...yourSeq],
      rivalSeq: [...rivalSeq],
      yourPath: [...yourPath],
      rivalPath: [...rivalPath],
      yourColor: 'red',
      rivalColor: 'blue',
      leftBackgroundTheme,
      rightBackgroundTheme,
    })
      .then(() => {
        if (!mounted) {
          s.destroy();
          return;
        }
        scene = s;
        sceneRef.current = s;
        setReady(true);
        onReady?.(s);
      })
      .catch((err) => {
        console.error('[PixiCanvas] init failed', err);
      });

    return () => {
      mounted = false;
      try {
        scene?.destroy();
      } catch {
        /* noop */
      }
      sceneRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'relative', width, height }}>
      {([
        { x: 0, theme: leftBackgroundTheme },
        { x: rightLaneX, theme: rightBackgroundTheme },
      ] as const).map(({ x, theme }) => (
        <div
          key={x}
          style={{
            position: 'absolute',
            top: HEADER_H,
            left: x,
            width: laneWidth,
            height: height - HEADER_H,
            overflow: 'hidden',
            borderRadius: 8,
            background: theme.cssBackground,
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.4)',
            pointerEvents: 'none',
          }}
        />
      ))}
      {!ready && (
        <div
          style={{
            position: 'absolute',
            zIndex: 2,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontFamily: 'var(--f-display)',
            fontSize: 11,
            letterSpacing: '0.2em',
            color: 'rgba(245,243,255,0.6)',
            pointerEvents: 'none',
          }}
        >
          LOADING WALL…
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ width, height, display: 'block', position: 'relative' }}
      />
    </div>
  );
}
